import React, { useState, useMemo } from 'react';
import { X, HelpCircle } from 'lucide-react';
import useGameStore from '../../game/gameStore';
import { PROBLEM_TYPES, ALLOCATION_IMPACTS } from '../../game/gameStore';

const PARTY_COLORS = {
  PT: '#e00', PL: '#003da5', MDB: '#f08000', PP: '#003c8f',
  'UNIÃO': '#2040a0', 'UNIÃO BRASIL': '#2040a0', PSD: '#00c040',
  PSDB: '#0040c0', PDT: '#c03030', PSB: '#ff6000',
  REPUBLICANOS: '#0060a0', PSOL: '#f0d000', PODE: '#00a050',
  NOVO: '#f06000', 'PCdoB': '#c00020', AVANTE: '#f08020',
  CIDADANIA: '#a040c0', SOLIDARIEDADE: '#f06020', PV: '#00a000',
  REDE: '#00b0a0', PRD: '#3366cc', DC: '#2020a0',
  'AGIR': '#40c080', 'MOBILIZA': '#aa6600',
};

const PLAYER_COLOR = '#4ade80';

function getPartyColor(partido) {
  if (!partido) return '#556';
  const upper = partido.toUpperCase();
  return PARTY_COLORS[upper] || `hsl(${[...upper].reduce((h, c) => h + c.charCodeAt(0), 0) % 360}, 55%, 50%)`;
}

function capitalize(s) {
  if (!s) return '';
  return s.replace(/\b\w+/g, w => w[0].toUpperCase() + w.slice(1).toLowerCase());
}

function SeverityDots({ severity }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <div key={i} className="w-1.5 h-1.5 rounded-full" style={{
          background: i <= severity
            ? severity >= 4 ? '#ef4444' : severity >= 3 ? '#f59e0b' : '#4ade80'
            : '#1e293b',
        }} />
      ))}
    </div>
  );
}

function BairroLegend() {
  return (
    <div className="space-y-3 text-left">
      <div>
        <div className="flex items-center gap-1.5 mb-1">
          <div className="w-2 h-2 rounded-full shrink-0" style={{ background: PLAYER_COLOR }} />
          <p className="text-[10px] font-bold text-white">Domínio</p>
        </div>
        <p className="text-[9px] text-gray-500 leading-relaxed">
          A faixa colorida no topo do card mostra quem <strong className="text-gray-400">domina</strong> aquele bairro hoje — o vereador com mais influência ali, com seu partido. Verde é o seu. Card sem faixa = bairro <strong className="text-gray-400">neutro</strong>, ainda sem dono.
        </p>
        <p className="text-[9px] text-gray-500 leading-relaxed mt-1.5">
          A segunda barra no rodapé do card (<span style={{ color: PLAYER_COLOR }}>verde</span>, "Você N%") mostra sua <strong className="text-gray-400">fatia de influência</strong> ali — sua força em relação a todos que disputam o bairro. Selecione o bairro para ver a disputa completa, vereador a vereador.
        </p>
      </div>

      <div>
        <div className="flex items-center gap-1.5 mb-1">
          <span className="text-[10px]">⚔️</span>
          <p className="text-[10px] font-bold text-white">Disputa</p>
        </div>
        <p className="text-[9px] text-gray-500 leading-relaxed">
          Investir nos problemas de um bairro aumenta a influência do seu vereador ali. Bairros neutros ou de rivais estão em disputa — continue investindo para conquistá-los, e continue defendendo os que já são seus, ou rivais podem retomar o controle.
        </p>
      </div>

      <div>
        <div className="flex items-center gap-1.5 mb-1">
          <span className="text-[10px]">{PROBLEM_TYPES.saude.emoji}</span>
          <p className="text-[10px] font-bold text-white">Problemas locais</p>
        </div>
        <p className="text-[9px] text-gray-500 leading-relaxed mb-1.5">
          Cada ícone é uma área carente do bairro. As bolinhas mostram a gravidade (verde leve, amarelo moderado, vermelho grave) — clique no bairro para investir e resolver.
        </p>
        <div className="flex flex-wrap gap-x-2 gap-y-1">
          {Object.values(PROBLEM_TYPES).map(p => (
            <span key={p.id} className="text-[8px] text-gray-500 flex items-center gap-0.5">
              <span>{p.emoji}</span>{p.label}
            </span>
          ))}
        </div>
        <p className="text-[9px] text-gray-500 leading-relaxed mt-1.5">
          <span className="text-green-400 font-bold">✓N</span> ao lado de um problema mostra quantas vezes você já investiu nele.
        </p>
      </div>

      <div>
        <div className="flex items-center gap-1.5 mb-1">
          <span className="text-[10px]">📊</span>
          <p className="text-[10px] font-bold text-white">Saúde do bairro</p>
        </div>
        <p className="text-[9px] text-gray-500 leading-relaxed">
          A barra e a % no rodapé do card resumem o bem-estar geral — sobe conforme os problemas são resolvidos.
        </p>
      </div>
    </div>
  );
}

// Player's share of the total political influence tracked in a territory,
// as a percentage — makes "how close am I to flipping this" legible instead
// of just showing who currently holds it.
function getInfluenceBreakdown(territory, playerVereadorId) {
  const inf = territory?.influence || {};
  const entries = Object.entries(inf).filter(([, v]) => v > 0.5);
  const total = entries.reduce((s, [, v]) => s + v, 0);
  const mine = Math.max(0, inf[playerVereadorId] || 0);
  return {
    total,
    playerPct: total > 0 ? Math.round((mine / total) * 100) : 0,
    entries: entries
      .map(([verId, val]) => ({ verId, value: val, pct: total > 0 ? Math.round((val / total) * 100) : 0 }))
      .sort((a, b) => b.value - a.value),
  };
}

function BairroCard({ territoryId, territory, isSelected, onSelect, controllingVer, isPlayerTerritory, playerVereadorId }) {
  const problems = territory.problems || {};
  const problemEntries = Object.entries(problems);
  const totalSeverity = problemEntries.reduce((s, [, p]) => s + p.severity, 0);
  const maxSeverity = problemEntries.length * 5;
  const healthPct = maxSeverity > 0 ? Math.round((1 - totalSeverity / maxSeverity) * 100) : 100;
  const { playerPct } = getInfluenceBreakdown(territory, playerVereadorId);

  const partyColor = controllingVer ? getPartyColor(controllingVer.partido) : '#1e293b';
  const borderColor = isSelected ? '#d4a574'
    : isPlayerTerritory ? PLAYER_COLOR
    : controllingVer ? partyColor
    : '#1a2540';

  const shortName = controllingVer
    ? capitalize(controllingVer.nome).split(' ').slice(0, 2).join(' ')
    : null;

  return (
    <button
      onClick={() => onSelect(territoryId)}
      className="relative text-left rounded-xl p-2 sm:p-2.5 transition-all duration-150 group"
      style={{
        background: isSelected ? '#111c30' : '#0a1424',
        border: `2px solid ${borderColor}`,
        boxShadow: isSelected ? `0 0 12px ${borderColor}44` : 'none',
      }}
    >
      {controllingVer && (
        <div className="absolute top-0 left-0 right-0 rounded-t-[10px] px-1.5 sm:px-2 py-[3px] flex items-center gap-1"
          style={{ background: `${partyColor}20`, borderBottom: `1px solid ${partyColor}33` }}>
          <div className="w-2 h-2 rounded-full shrink-0" style={{ background: isPlayerTerritory ? PLAYER_COLOR : partyColor }} />
          <span className="text-[7px] sm:text-[8px] font-bold truncate" style={{ color: isPlayerTerritory ? PLAYER_COLOR : partyColor }}>
            {shortName}
          </span>
          <span className="text-[6px] sm:text-[7px] font-bold shrink-0 ml-auto px-1 py-[1px] rounded" style={{
            background: `${partyColor}30`, color: partyColor,
          }}>
            {controllingVer.partido}
          </span>
        </div>
      )}

      <div className={`flex items-center justify-between mb-0.5 gap-1 ${controllingVer ? 'mt-4 sm:mt-5' : ''}`}>
        <p className="text-[10px] sm:text-[11px] font-bold text-white truncate flex-1">
          {territory.name}
        </p>
      </div>

      <p className="text-[8px] sm:text-[9px] text-gray-500 mb-1.5">
        {territory.population?.toLocaleString('pt-BR')} hab
      </p>

      <div className="space-y-0.5 sm:space-y-1">
        {problemEntries.map(([type, prob]) => {
          const def = PROBLEM_TYPES[type];
          if (!def) return null;
          return (
            <div key={type} className="flex items-center gap-1 sm:gap-1.5">
              <span className="text-[9px] sm:text-[10px] w-3.5 sm:w-4 text-center">{def.emoji}</span>
              <SeverityDots severity={prob.severity} />
              {prob.invested > 0 && (
                <span className="text-[7px] sm:text-[8px] text-green-400 ml-auto">✓{prob.invested}</span>
              )}
            </div>
          );
        })}
      </div>

      <div className="mt-1.5 flex items-center gap-1.5">
        <div className="flex-1 h-1 rounded-full bg-[#1e293b] overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{
            width: `${healthPct}%`,
            background: healthPct > 70 ? '#4ade80' : healthPct > 40 ? '#f59e0b' : '#ef4444',
          }} />
        </div>
        <span className="text-[7px] sm:text-[8px] text-gray-500">{healthPct}%</span>
      </div>

      <div className="mt-1 flex items-center gap-1.5">
        <div className="flex-1 h-1 rounded-full bg-[#1e293b] overflow-hidden">
          <div className="h-full rounded-full transition-all duration-500" style={{ width: `${playerPct}%`, background: PLAYER_COLOR }} />
        </div>
        <span className="text-[7px] sm:text-[8px]" style={{ color: PLAYER_COLOR }}>Você {playerPct}%</span>
      </div>
    </button>
  );
}

function ProblemAllocationPanel({ territory, controllingVer, isPlayerTerritory, onAllocate, playerMoney, vereadorMap, playerVereadorId }) {
  const problems = territory?.problems || {};
  const problemEntries = Object.entries(problems);

  if (problemEntries.length === 0) {
    return (
      <div className="p-4 text-center text-gray-500 text-xs">
        Nenhum problema identificado neste bairro
      </div>
    );
  }

  const partyColor = controllingVer ? getPartyColor(controllingVer.partido) : '#556';
  const { entries: influenceEntries } = getInfluenceBreakdown(territory, playerVereadorId);

  return (
    <div className="space-y-2">
      <div className="mb-3">
        <p className="text-[11px] font-bold text-white">{territory.name}</p>
        <p className="text-[9px] text-gray-500 mt-0.5">
          {territory.population?.toLocaleString('pt-BR')} hab
        </p>
        {controllingVer && (
          <div className="flex items-center gap-1.5 mt-1.5 px-2 py-1 rounded-lg"
            style={{ background: `${partyColor}15`, border: `1px solid ${partyColor}25` }}>
            <div className="w-2 h-2 rounded-full" style={{ background: isPlayerTerritory ? PLAYER_COLOR : partyColor }} />
            <span className="text-[9px] font-bold" style={{ color: isPlayerTerritory ? PLAYER_COLOR : partyColor }}>
              {capitalize(controllingVer.nome).split(' ').slice(0, 3).join(' ')}
            </span>
            <span className="text-[8px] ml-auto" style={{ color: partyColor }}>
              {controllingVer.partido}
            </span>
          </div>
        )}
        {!controllingVer && (
          <div className="flex items-center gap-1.5 mt-1.5 px-2 py-1 rounded-lg bg-white/[.03] border border-white/[.06]">
            <span className="text-[9px] text-gray-500">⚪ Território neutro</span>
          </div>
        )}

        {influenceEntries.length > 0 && (
          <div className="mt-2 p-2 rounded-lg bg-white/[.02] border border-white/[.06]">
            <p className="text-[8px] text-gray-600 uppercase tracking-wider font-bold mb-1.5">Influência no bairro</p>
            <div className="space-y-1">
              {influenceEntries.slice(0, 4).map((e) => {
                const isPlayer = e.verId === playerVereadorId;
                const ver = vereadorMap?.[e.verId];
                const pc = isPlayer ? PLAYER_COLOR : (ver ? getPartyColor(ver.partido) : '#556');
                const label = isPlayer ? 'Você' : (ver ? capitalize(ver.nome).split(' ').slice(0, 2).join(' ') : 'Outro vereador');
                return (
                  <div key={e.verId} className="flex items-center gap-1.5">
                    <span className={`text-[8px] w-16 shrink-0 truncate ${isPlayer ? 'font-bold' : ''}`} style={{ color: pc }}>{label}</span>
                    <div className="flex-1 h-1 rounded-full bg-[#1e293b] overflow-hidden">
                      <div className="h-full rounded-full transition-all duration-500" style={{ width: `${e.pct}%`, background: pc }} />
                    </div>
                    <span className="text-[8px] text-gray-500 w-7 text-right shrink-0">{e.pct}%</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {problemEntries.map(([type, prob]) => {
        const def = PROBLEM_TYPES[type];
        const impact = ALLOCATION_IMPACTS[type];
        if (!def || !impact) return null;

        const cost = Math.abs(impact.money);
        const canAfford = playerMoney >= cost;
        const solved = prob.severity <= 0;

        return (
          <div key={type} className="rounded-lg p-2 sm:p-2.5" style={{
            background: '#0f1a30',
            border: `1px solid ${solved ? '#1a3050' : def.color}33`,
          }}>
            <div className="flex items-center justify-between mb-1.5">
              <div className="flex items-center gap-2">
                <span className="text-sm sm:text-base">{def.emoji}</span>
                <div>
                  <p className="text-[10px] sm:text-[11px] font-bold" style={{ color: def.color }}>{def.label}</p>
                  <SeverityDots severity={prob.severity} />
                </div>
              </div>
              {prob.invested > 0 && (
                <span className="text-[8px] sm:text-[9px] text-green-400 bg-green-900/30 px-1.5 py-0.5 rounded">
                  ✓ {prob.invested}x
                </span>
              )}
            </div>

            <p className="text-[8px] sm:text-[9px] text-gray-400 mb-2">{impact.desc}</p>

            <div className="flex flex-wrap gap-1 mb-2">
              <span className="text-[7px] sm:text-[8px] px-1 sm:px-1.5 py-0.5 rounded bg-purple-900/30 text-purple-300">
                Vereador +{impact.vereadorCarisma}
              </span>
              <span className="text-[7px] sm:text-[8px] px-1 sm:px-1.5 py-0.5 rounded bg-green-900/30 text-green-300">
                Você +{impact.assessorCarisma}
              </span>
              {impact.influence > 0 && (
                <span className="text-[7px] sm:text-[8px] px-1 sm:px-1.5 py-0.5 rounded bg-blue-900/30 text-blue-300">
                  Infl +{impact.influence}
                </span>
              )}
              {impact.risk > 0 && (
                <span className="text-[7px] sm:text-[8px] px-1 sm:px-1.5 py-0.5 rounded bg-red-900/30 text-red-300">
                  Risco +{impact.risk}
                </span>
              )}
            </div>

            <button
              disabled={!canAfford || solved}
              onClick={() => onAllocate(type)}
              className={`w-full py-1.5 rounded-lg text-[10px] font-bold transition-all ${
                solved
                  ? 'bg-green-900/20 text-green-600 border border-green-800/30 cursor-not-allowed'
                  : canAfford
                    ? 'bg-[#12203a] border border-[#2a4060] hover:bg-[#1a2a44] text-white cursor-pointer'
                    : 'bg-[#0a0e18] border border-[#111828] text-gray-600 cursor-not-allowed'
              }`}
            >
              {solved ? '✅ Resolvido' : `Investir R$ ${cost}K`}
            </button>
          </div>
        );
      })}
    </div>
  );
}

export default function TerritoryGrid({ onTerritoryClick, selectedTerritory, onConfirmResolve }) {
  const territories = useGameStore(s => s.territories);
  const player = useGameStore(s => s.player);
  const turnState = useGameStore(s => s.turnState);
  const allocateProblem = useGameStore(s => s.allocateProblem);
  const lastDecision = useGameStore(s => s.lastDecision);
  const vereadores = useGameStore(s => s.vereadores);
  const playerVereadorId = useGameStore(s => s.playerVereadorId);

  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [vereadorFilter, setVereadorFilter] = useState(null);
  const [showLegend, setShowLegend] = useState(false);

  const vereadorMap = useMemo(() => {
    const m = {};
    for (const v of vereadores) m[v.id] = v;
    return m;
  }, [vereadores]);

  const vereadorRanking = useMemo(() => {
    const counts = {};
    for (const t of Object.values(territories)) {
      if (t.controlledBy) counts[t.controlledBy] = (counts[t.controlledBy] || 0) + 1;
    }
    return Object.entries(counts)
      .map(([id, count]) => ({ ver: vereadorMap[id], count }))
      .filter(r => r.ver)
      .sort((a, b) => b.count - a.count);
  }, [territories, vereadorMap]);

  const partyStats = useMemo(() => {
    const map = {};
    for (const r of vereadorRanking) {
      const p = r.ver.partido;
      if (!map[p]) map[p] = { partido: p, count: 0, color: getPartyColor(p) };
      map[p].count += r.count;
    }
    return Object.values(map).sort((a, b) => b.count - a.count);
  }, [vereadorRanking]);

  const territoryList = useMemo(() => {
    let list = Object.entries(territories).map(([id, t]) => ({ ...t, id }));

    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      list = list.filter(t => t.name.toLowerCase().includes(term));
    }

    if (filter === 'mine') list = list.filter(t => t.controlledBy === playerVereadorId);
    else if (filter === 'rival') list = list.filter(t => t.controlledBy && t.controlledBy !== playerVereadorId);
    else if (filter === 'neutral') list = list.filter(t => !t.controlledBy);
    else if (filter === 'critical') list = list.filter(t => Object.values(t.problems || {}).some(p => p.severity >= 4));

    if (vereadorFilter) {
      list = list.filter(t => t.controlledBy === vereadorFilter);
    }

    return list;
  }, [territories, searchTerm, filter, vereadorFilter, playerVereadorId]);

  const stats = useMemo(() => {
    const all = Object.values(territories);
    return {
      total: all.length,
      mine: all.filter(t => t.controlledBy === playerVereadorId).length,
      rival: all.filter(t => t.controlledBy && t.controlledBy !== playerVereadorId).length,
      neutral: all.filter(t => !t.controlledBy).length,
      critical: all.filter(t => Object.values(t.problems || {}).some(p => p.severity >= 4)).length,
    };
  }, [territories, playerVereadorId]);

  const selectedT = selectedTerritory ? territories[selectedTerritory] : null;
  const selectedVer = selectedT?.controlledBy ? vereadorMap[selectedT.controlledBy] : null;

  const handleAllocate = (problemType) => {
    if (selectedTerritory) allocateProblem(selectedTerritory, problemType);
  };

  const handleClosePanel = () => {
    onTerritoryClick(null);
  };

  return (
    <div className="w-full h-full flex flex-col md:flex-row bg-[#060c18] overflow-hidden relative">
      {/* Main Grid */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Party bar */}
        {partyStats.length > 0 && (
          <div className="px-2 sm:px-3 py-1.5 border-b border-white/[.04] shrink-0">
            <div className="flex h-2 sm:h-2.5 rounded-full overflow-hidden bg-[#0e1628]">
              {partyStats.map(p => (
                <div
                  key={p.partido}
                  className="h-full transition-all duration-700 cursor-pointer hover:brightness-125"
                  style={{
                    width: `${(p.count / Math.max(1, stats.total)) * 100}%`,
                    background: p.color,
                    opacity: vereadorFilter ? 0.3 : 0.85,
                  }}
                  title={`${p.partido}: ${p.count} bairros`}
                  onClick={() => {
                    const verOfParty = vereadores.find(v => v.partido === p.partido);
                    setVereadorFilter(vf => vf ? null : verOfParty?.id || null);
                    setFilter('all');
                  }}
                />
              ))}
              {stats.neutral > 0 && (
                <div className="h-full" style={{ width: `${(stats.neutral / Math.max(1, stats.total)) * 100}%`, background: '#1e293b' }} />
              )}
            </div>
            <div className="flex flex-wrap gap-x-2 sm:gap-x-3 gap-y-0.5 mt-1">
              {partyStats.slice(0, 6).map(p => (
                <span key={p.partido} className="text-[7px] sm:text-[8px] flex items-center gap-0.5 sm:gap-1 cursor-pointer hover:opacity-80"
                  onClick={() => {
                    const verOfParty = vereadores.find(v => v.partido === p.partido);
                    setVereadorFilter(vf => vf ? null : verOfParty?.id || null);
                    setFilter('all');
                  }}>
                  <span className="w-1.5 h-1.5 rounded-full inline-block" style={{ background: p.color }} />
                  <span style={{ color: p.color }}>{p.partido}</span>
                  <span className="text-gray-600">{p.count}</span>
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Filters — horizontal scroll on mobile */}
        <div className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 border-b border-white/[.04] shrink-0 overflow-x-auto" style={{ scrollbarWidth: 'none', WebkitOverflowScrolling: 'touch' }}>
          <input
            type="text"
            placeholder="Buscar..."
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            className="text-[10px] bg-[#0a1424] border border-[#1a2848] rounded-lg px-2 py-1.5 text-white placeholder-gray-600 outline-none focus:border-[#2a4060] w-20 sm:w-32 shrink-0"
          />
          {[
            { key: 'all', label: `${stats.total}`, mobile: 'Todos' },
            { key: 'mine', label: `🟢${stats.mine}`, mobile: 'Meu' },
            { key: 'rival', label: `🔴${stats.rival}`, mobile: 'Rival' },
            { key: 'neutral', label: `⚪${stats.neutral}`, mobile: 'Neutro' },
            { key: 'critical', label: `⚠️${stats.critical}`, mobile: 'Crit' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => { setFilter(f.key); setVereadorFilter(null); }}
              className={`text-[8px] sm:text-[9px] px-1.5 sm:px-2 py-1 rounded-lg transition-all whitespace-nowrap shrink-0 ${
                filter === f.key && !vereadorFilter
                  ? 'bg-[#1a2848] text-white font-bold border border-[#2a4060]'
                  : 'text-gray-500 hover:text-gray-300 border border-transparent'
              }`}
            >
              <span className="hidden sm:inline">{f.mobile} ({f.label.replace(/[^\d]/g, '') || stats.total})</span>
              <span className="sm:hidden">{f.label}</span>
            </button>
          ))}

          {vereadorRanking.length > 0 && (
            <select
              value={vereadorFilter || ''}
              onChange={e => { setVereadorFilter(e.target.value || null); if (e.target.value) setFilter('all'); }}
              className="text-[8px] sm:text-[9px] bg-[#0a1424] border border-[#1a2848] rounded-lg px-1.5 sm:px-2 py-1 text-gray-400 outline-none shrink-0"
            >
              <option value="">Vereadores</option>
              {vereadorRanking.map(r => (
                <option key={r.ver.id} value={r.ver.id}>
                  {capitalize(r.ver.nome).split(' ').slice(0, 2).join(' ')} ({r.ver.partido}) — {r.count}
                </option>
              ))}
            </select>
          )}
        </div>

        {/* Dual charisma bar */}
        <div className="flex items-center gap-2 sm:gap-3 px-2 sm:px-3 py-1 sm:py-1.5 border-b border-white/[.04] shrink-0">
          <div className="flex items-center gap-1 sm:gap-1.5 flex-1 min-w-0">
            <span className="text-[8px] sm:text-[9px] text-purple-400 shrink-0">🏛️</span>
            <div className="flex-1 h-1 sm:h-1.5 bg-[#1e293b] rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-purple-500 transition-all" style={{ width: `${Math.min(100, player.vereadorCarisma || 0)}%` }} />
            </div>
            <span className="text-[9px] sm:text-[10px] font-bold text-purple-300 shrink-0">{player.vereadorCarisma || 0}</span>
          </div>
          <div className="flex items-center gap-1 sm:gap-1.5 flex-1 min-w-0">
            <span className="text-[8px] sm:text-[9px] text-green-400 shrink-0">📎</span>
            <div className="flex-1 h-1 sm:h-1.5 bg-[#1e293b] rounded-full overflow-hidden">
              <div className="h-full rounded-full bg-green-500 transition-all" style={{ width: `${Math.min(100, player.popularity)}%` }} />
            </div>
            <span className="text-[9px] sm:text-[10px] font-bold text-green-300 shrink-0">{player.popularity}</span>
          </div>
        </div>

        {/* Last action feedback */}
        {lastDecision?.problemType && (
          <div className="flex items-center justify-center gap-2 sm:gap-3 px-2 py-1 border-b border-white/[.04] shrink-0">
            <span className="text-[9px] sm:text-[10px] font-bold text-purple-300">
              Ver +{lastDecision.vereadorCarisma}
            </span>
            <span className="text-[9px] sm:text-[10px] font-bold text-green-300">
              Você +{lastDecision.pop}
            </span>
            {lastDecision.risk > 0 && (
              <span className="text-[9px] sm:text-[10px] font-bold text-red-300">
                ⚠+{lastDecision.risk}
              </span>
            )}
          </div>
        )}

        {/* Grid */}
        <div className="flex-1 overflow-y-auto p-2 sm:p-3" style={{ scrollbarWidth: 'thin' }}>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-1.5 sm:gap-2">
            {territoryList.map(t => (
              <BairroCard
                key={t.id}
                territoryId={t.id}
                territory={t}
                isSelected={selectedTerritory === t.id}
                onSelect={onTerritoryClick}
                controllingVer={t.controlledBy ? vereadorMap[t.controlledBy] : null}
                isPlayerTerritory={t.controlledBy === playerVereadorId}
                playerVereadorId={playerVereadorId}
              />
            ))}
          </div>
          {territoryList.length === 0 && (
            <div className="flex items-center justify-center h-32 text-gray-600 text-xs">
              Nenhum bairro encontrado
            </div>
          )}
        </div>

        {/* Mobile: sticky bottom bar with money + resolve */}
        {turnState === 'map' && !selectedT && (
          <div className="md:hidden p-2 border-t border-white/[.04] bg-[#080e1a] shrink-0 flex items-center gap-2">
            <span className="text-[9px] text-gray-500 flex-1">💰 R$ {player.money}K</span>
            <button
              onClick={onConfirmResolve}
              className="px-4 py-2 rounded-lg font-bold text-xs bg-gradient-to-r from-[#1a5c3a] to-[#0a3c2a] border border-[#2a7c4a] text-green-300"
            >
              ⚡ Resolver Turno
            </button>
          </div>
        )}
      </div>

      {/* Desktop: Right panel */}
      {turnState === 'map' && (
        <div className="hidden md:flex w-72 border-l border-white/[.04] bg-[#080e1a] overflow-y-auto shrink-0 flex-col" style={{ scrollbarWidth: 'thin' }}>
          {selectedT ? (
            <div className="p-3 flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
              <div className="flex items-center justify-between mb-3">
                <p className="text-[9px] text-gray-600 uppercase tracking-wider font-bold">
                  Investir no Bairro
                </p>
                <button onClick={() => setShowLegend(v => !v)}
                  className={`flex items-center gap-1 text-[8px] font-bold px-1.5 py-0.5 rounded transition-colors ${showLegend ? 'text-[#d4a574] bg-[#d4a574]/10' : 'text-gray-600 hover:text-gray-400'}`}>
                  <HelpCircle size={10} /> Como funciona?
                </button>
              </div>
              {showLegend && (
                <div className="mb-3 p-2.5 rounded-lg bg-white/[.03] border border-white/[.06]">
                  <BairroLegend />
                </div>
              )}
              <ProblemAllocationPanel
                territory={selectedT}
                controllingVer={selectedVer}
                isPlayerTerritory={selectedT.controlledBy === playerVereadorId}
                onAllocate={handleAllocate}
                playerMoney={player.money}
                vereadorMap={vereadorMap}
                playerVereadorId={playerVereadorId}
              />
            </div>
          ) : (
            <div className="p-3 flex-1 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
              <div className="text-center mb-4">
                <p className="text-gray-500 text-xs mb-1">Selecione um bairro</p>
                <p className="text-gray-600 text-[10px]">Clique em um bairro para investir e conquistar território</p>
              </div>
              <div className="p-2.5 rounded-lg bg-white/[.03] border border-white/[.06]">
                <BairroLegend />
              </div>
            </div>
          )}

          {/* Ranking */}
          <div className="px-3 py-2 border-t border-white/[.04] shrink-0">
            <p className="text-[8px] text-gray-600 uppercase tracking-wider font-bold mb-1.5">Ranking Domínio</p>
            <div className="space-y-0.5 max-h-28 overflow-y-auto" style={{ scrollbarWidth: 'thin' }}>
              {vereadorRanking.slice(0, 8).map((r, i) => {
                const isPlayer = r.ver.id === playerVereadorId;
                const pc = getPartyColor(r.ver.partido);
                return (
                  <div key={r.ver.id}
                    className={`flex items-center gap-1.5 text-[9px] py-0.5 px-1 rounded ${isPlayer ? 'bg-green-900/20' : ''}`}>
                    <span className="text-gray-600 w-3 text-right font-mono">{i + 1}</span>
                    <div className="w-2 h-2 rounded-full shrink-0" style={{ background: isPlayer ? PLAYER_COLOR : pc }} />
                    <span className={`truncate flex-1 ${isPlayer ? 'text-green-400 font-bold' : 'text-gray-400'}`}>
                      {capitalize(r.ver.nome).split(' ').slice(0, 2).join(' ')}
                    </span>
                    <span className="text-[7px] shrink-0" style={{ color: pc }}>{r.ver.partido}</span>
                    <span className="text-gray-500 font-mono shrink-0 w-4 text-right">{r.count}</span>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="p-3 border-t border-white/[.04] shrink-0">
            <div className="flex items-center justify-between mb-2">
              <span className="text-[9px] text-gray-500">💰 R$ {player.money}K disponível</span>
            </div>
            <button
              onClick={onConfirmResolve}
              className="w-full py-2.5 rounded-lg font-bold text-sm bg-gradient-to-r from-[#1a5c3a] to-[#0a3c2a] border border-[#2a7c4a] text-green-300 hover:from-[#2a6c4a] hover:to-[#1a4c3a] transition-all"
            >
              ⚡ Resolver Turno
            </button>
          </div>
        </div>
      )}

      {/* Mobile: Bottom sheet when territory is selected */}
      {turnState === 'map' && selectedT && (
        <div className="md:hidden fixed inset-x-0 bottom-0 z-40 animate-[slideUp_0.25s_ease-out]">
          <div className="bg-[#080e1a] border-t border-white/[.08] rounded-t-2xl max-h-[70vh] flex flex-col"
            style={{ boxShadow: '0 -8px 30px rgba(0,0,0,0.5)' }}>
            {/* Handle + close */}
            <div className="flex items-center justify-between px-4 pt-3 pb-1 shrink-0">
              <div className="w-8 h-1 rounded-full bg-white/10 mx-auto" />
              <button onClick={() => setShowLegend(v => !v)}
                className={`absolute left-3 top-2.5 flex items-center gap-1 text-[8px] font-bold px-1.5 py-1 rounded ${showLegend ? 'text-[#d4a574] bg-[#d4a574]/10' : 'text-gray-500'}`}>
                <HelpCircle size={12} />
              </button>
              <button onClick={handleClosePanel} className="absolute right-3 top-3 p-1 rounded-full hover:bg-white/10">
                <X size={16} className="text-gray-500" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-4 pb-2" style={{ scrollbarWidth: 'thin' }}>
              {showLegend && (
                <div className="mb-3 p-2.5 rounded-lg bg-white/[.03] border border-white/[.06]">
                  <BairroLegend />
                </div>
              )}
              <ProblemAllocationPanel
                territory={selectedT}
                controllingVer={selectedVer}
                isPlayerTerritory={selectedT.controlledBy === playerVereadorId}
                onAllocate={handleAllocate}
                playerMoney={player.money}
                vereadorMap={vereadorMap}
                playerVereadorId={playerVereadorId}
              />
            </div>

            <div className="px-4 py-3 border-t border-white/[.04] shrink-0 flex items-center gap-2">
              <span className="text-[9px] text-gray-500 flex-1">💰 R$ {player.money}K</span>
              <button
                onClick={onConfirmResolve}
                className="px-5 py-2.5 rounded-lg font-bold text-xs bg-gradient-to-r from-[#1a5c3a] to-[#0a3c2a] border border-[#2a7c4a] text-green-300"
              >
                ⚡ Resolver Turno
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
